import {useQuery} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import staticURL from 'lib/staticURL';
import {FONT_PROXIMA_14, FONT_PROXIMA_15, FONT_PROXIMA_17} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_LIGHT, GRAY_LIGHTER, ORANGE_LIGHTEST, WHITE, BLUE, ORANGE} from 'ui-library/Colors';
import {LocalTime} from 'ui-library/Times';

import type {
  GenerativeModelRunsQuery,
  GenerativeModelRunsQueryVariables,
} from '../__generated/GenerativeModelRunsQuery';
import {GENERATIVE_MODEL_RUNS_QUERY} from '../ModelHubQueries';
import {BoltzGenTargetType} from './GenerativeModelsConstants';

export type GenerativeModelRun = GenerativeModelRunsQuery['generativeModelRuns'][number];

function MetadataItem({
  label,
  value,
  className,
}: {
  label: string;
  value: string | JSX.Element;
  className?: object;
}): JSX.Element {
  return (
    <div className={css(styles.metadataItem, className)}>
      <span className={css(styles.metadataLabel)}>{label}</span>
      <span className={css(styles.metadataValue)}>{value}</span>
    </div>
  );
}

function MetadataRow({date}: {date: string}): JSX.Element {
  return (
    <div className={css(styles.metadataRow)}>
      <MetadataItem label="Date" value={<LocalTime datetime={date} />} className={styles.dateItem} />
    </div>
  );
}

function PendingRunItem({run}: {run: GenerativeModelRun}): JSX.Element {
  return (
    <div className={css(styles.runItem)}>
      <div className={css(styles.runIcon)}>
        <div className={css(styles.iconCircle, styles.inProgressIcon)}>
          <div className={css(styles.spinnerIcon)} />
        </div>
      </div>
      <div className={css(styles.runContent)}>
        <MetadataRow date={run.createdAt} />
      </div>
    </div>
  );
}

function CompletedRunItem({
  run,
  onRunSelected,
}: {
  run: GenerativeModelRun;
  onRunSelected: (run: GenerativeModelRun) => void;
}): JSX.Element {
  const handleClick = (): void => {
    onRunSelected(run);
  };
  return (
    <div className={css(styles.runItem, styles.clickableRunItem)} onClick={handleClick}>
      <div className={css(styles.runIcon)}>
        <div className={css(styles.iconCircle)}>
          <img
            src={staticURL('/static/img/model_hub/boltz2.png')}
            alt="Boltz icon"
            className={css(styles.modelIcon)}
          />
        </div>
      </div>
      <div className={css(styles.runContent)}>
        <MetadataRow date={run.createdAt} />
      </div>
    </div>
  );
}

export default function GenerativeModelsRunsListWrapper({
  currentEntityType,
  targetId,
  onCreateNewRun,
  onRunSelected,
}: {
  currentEntityType: BoltzGenTargetType;
  targetId: string;
  onCreateNewRun: () => void;
  onRunSelected: (run: GenerativeModelRun) => void;
}): JSX.Element {
  const {data, loading, error} = useQuery<GenerativeModelRunsQuery, GenerativeModelRunsQueryVariables>(
    GENERATIVE_MODEL_RUNS_QUERY,
    {
      variables: {
        proteinId: currentEntityType === ModelTypeConstants.PROTEIN ? targetId : undefined,
        moleculeId: currentEntityType === ModelTypeConstants.MOLECULE ? targetId : undefined,
      },
      pollInterval: 45000,
    }
  );
  const sortedPendingRuns = useMemo(
    () =>
      data?.generativeModelRuns
        ?.filter((run) => run.status === 'PENDING' || run.status === 'RUNNING')
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [data?.generativeModelRuns]
  );

  const sortedCompletedRuns = useMemo(
    () =>
      data?.generativeModelRuns
        ?.filter((run) => run.status === 'SUCCEEDED')
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [data?.generativeModelRuns]
  );

  if (loading) {
    return <div>Loading...</div>;
  }
  if (error || !data) {
    return <div>Error: {error?.message || 'Failed to load runs'}</div>;
  }
  return (
    <GenerativeModelsRunsList
      pendingRuns={sortedPendingRuns}
      completedRuns={sortedCompletedRuns}
      onCreateNewRun={onCreateNewRun}
      onRunSelected={onRunSelected}
    />
  );
}

export function GenerativeModelsRunsList({
  pendingRuns = [],
  completedRuns = [],
  onCreateNewRun,
  onRunSelected = (run: GenerativeModelRun) => {},
}: {
  pendingRuns?: Array<GenerativeModelRun>;
  completedRuns?: Array<GenerativeModelRun>;
  onCreateNewRun: () => void;
  onRunSelected: (run: GenerativeModelRun) => void;
}): JSX.Element {
  return (
    <div className={css(styles.runsListWrapper)}>
      <div className={css(styles.runsContent)}>
        <div className={css(styles.runsHeader)}>
          <h2 className={css(styles.runsTitle)}>BoltzGen Runs</h2>
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            icon="sparkle"
            size={ButtonSize.MEDIUM}
            text="Create new run"
            onClick={onCreateNewRun}
          />
        </div>

        <h3 className={css(styles.sectionTitle)}>Pending runs</h3>
        {pendingRuns?.length && pendingRuns.length > 0 ? (
          pendingRuns.map((run) => <PendingRunItem key={run.id} run={run} />)
        ) : (
          <div className={css(styles.runItem)}>No pending runs.</div>
        )}

        <h3 className={css(styles.sectionTitle, styles.completedRunsTitle)}>Completed runs</h3>
        {completedRuns?.length && completedRuns.length > 0 ? (
          completedRuns.map((run) => (
            <CompletedRunItem key={run.id} run={run} onRunSelected={onRunSelected} />
          ))
        ) : (
          <div className={css(styles.runItem)}>No completed runs.</div>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  runsListWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  runsContent: {
    flex: '1 1 auto',
    padding: '20px',
    overflowY: 'auto',
  },
  runsHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  runsTitle: {
    ...FONT_PROXIMA_17,
    fontWeight: 'bold',
    margin: '0',
  },
  sectionTitle: {
    ...FONT_PROXIMA_15,
    fontWeight: 'bold',
    margin: '0 0 16px 0',
  },
  runItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: '8px',
    border: `1px solid ${GRAY_LIGHT}`,
    marginBottom: '8px',
    backgroundColor: WHITE,
  },
  clickableRunItem: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
      borderColor: GRAY_LIGHT,
    },
    ':focus': {
      outline: `2px solid ${BLUE}`,
      outlineOffset: '2px',
    },
  },
  runIcon: {
    marginRight: '12px',
  },
  iconCircle: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: GRAY_LIGHTER,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inProgressIcon: {
    backgroundColor: ORANGE_LIGHTEST,
  },
  runContent: {
    flex: '1 1 auto',
  },
  metadataRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '30px',
    marginLeft: '16px',
  },
  metadataItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
  },
  metadataLabel: {
    ...FONT_PROXIMA_14,
    color: GRAY_DARKER,
    fontWeight: 'bold',
  },
  metadataValue: {
    ...FONT_PROXIMA_14,
    color: GRAY_DARKER,
  },
  spinnerIcon: {
    width: '20px',
    height: '20px',
    border: `2px solid ${ORANGE}`,
    borderTop: '2px solid transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  modelIcon: {
    width: '20px',
    height: '20px',
    objectFit: 'contain',
  },
  dateItem: {
    minWidth: '130px',
  },
  completedRunsTitle: {
    marginTop: '32px',
  },
});
